import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Speaker, Story } from 'graphql/generated/graphql';

export type QueueStory = Pick<Story, 'id' | 'title'> & {
  speaker: Pick<Speaker, 'name'>;
};

export type QueueMoveKind = 'queue' | 'recommendation';

export interface QueueState {
  history: QueueStory[];
  queue: QueueStory[];
  recommendations: QueueStory[];
}

export const initialQueueState: QueueState = {
  history: [],
  queue: [],
  recommendations: [],
};

export const queueSlice = createSlice({
  name: 'queue',
  initialState: initialQueueState,
  reducers: {
    addToQueue: (state, action: PayloadAction<QueueStory>) => {
      state.queue = [...state.queue, action.payload];
    },
    playPrevious: (state) => {
      if (state.history.length > 1) {
        state.history.splice(state.history.length - 1, 1);
      }
    },
    playNext: (state) => {
      if (state.queue.length > 0) {
        state.history.push(state.queue[0]);
        state.queue.splice(0, 1);
      } else if (state.recommendations.length > 0) {
        state.history.push(state.recommendations[0]);
        state.recommendations.splice(0, 1);
      }
    },
    playAudioAndClearHistory: (state, action: PayloadAction<QueueStory>) => {
      state.history = [action.payload];
      state.recommendations = [];
    },
    setRecommendations: (state, action: PayloadAction<QueueStory[]>) => {
      state.recommendations = action.payload;
    },
    removeFromQueue: (state, action: PayloadAction<number>) => {
      state.queue.splice(action.payload, 1);
    },
    removeAllFromQueue: (state) => {
      state.queue = [];
    },
    removeFromRecommendations: (state, action: PayloadAction<number>) => {
      state.recommendations.splice(action.payload, 1);
    },
    removeAllFromRecommendations: (state) => {
      state.recommendations = [];
    },
    reorder: (
      state,
      action: PayloadAction<{
        from: number;
        fromKind: QueueMoveKind;
        to: number;
        toKind: QueueMoveKind;
      }>,
    ) => {
      const itemToMove = (action.payload.fromKind === 'queue'
        ? state.queue
        : state.recommendations
      ).splice(action.payload.from, 1);

      (action.payload.toKind === 'queue'
        ? state.queue
        : state.recommendations
      ).splice(action.payload.to, 0, itemToMove[0]);
    },
    playAudioFromQueue: (state, action: PayloadAction<number>) => {
      const item = state.queue[action.payload];

      if (item) {
        state.history.push(item);
        state.queue.splice(0, action.payload + 1);
      }
    },
    playAudioFromRecommendations: (state, action: PayloadAction<number>) => {
      const item = state.recommendations[action.payload];

      if (item) {
        state.history.push(item);
        state.recommendations.splice(0, action.payload + 1);
      }
    },
  },
});

export const getPreviousStoryToPlay = ({
  queue,
}: {
  queue: QueueState;
}): QueueStory | undefined => queue.history[queue.history.length - 2];

export const getNextStoryToPlay = ({
  queue,
}: {
  queue: QueueState;
}): QueueStory | undefined => [...queue.queue, ...queue.recommendations][0];

export const {
  playAudioAndClearHistory,
  addToQueue,
  setRecommendations,
  playNext,
  playPrevious,
  removeFromQueue,
  removeAllFromQueue,
  removeFromRecommendations,
  removeAllFromRecommendations,
  reorder,
  playAudioFromQueue,
  playAudioFromRecommendations,
} = queueSlice.actions;

export default queueSlice.reducer;
